/**
 * The save. One mutation for all three types, because the only thing that
 * differs is the request `entryBody` already built.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";
import { entryBody, type EntryDraft } from "./draft";
import { calcPaise } from "./money";
import type { EntryType } from "./types";

export interface SaveVariables {
  draft: EntryDraft;
  /** Present = update. Absent = create — which is what a duplicate is. */
  recordId?: string;
}

export interface SaveResult {
  id: string;
  type: EntryType;
  amountPaise: number;
}

/**
 * Query keys every affected screen reads. Invalidated as a set, because a
 * transfer moves two account balances and a record shows up in the dashboard,
 * the records list and the per-domain list at once.
 */
const AFFECTED_KEYS = [
  "income",
  "expenses",
  "expenses-all-for-stats",
  "transfers",
  "accounts",
  "records",
  "dashboard",
  "suggestions",
  "budgets",
  // The Wallet records list, the shell's account chip/balances and the
  // account page's ledger + sections read these — without them an edited or
  // new record stayed invisible until a reload.
  "wallet-records",
  "wallet-shell-accounts",
  "account-ledger",
  "account-summary",
  "wallet-stats",
];

async function readError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as
    | { error?: string; detail?: unknown }
    | null;
  if (typeof body?.error === "string") return body.error;
  if (typeof body?.detail === "string") return body.detail;
  // FastAPI validation errors arrive as a list of {loc, msg}; show the first
  // rather than "[object Object]".
  if (Array.isArray(body?.detail)) {
    const first = body.detail[0] as { msg?: string } | undefined;
    if (first?.msg) return first.msg;
  }
  return `Could not save (${res.status})`;
}

export function useSaveEntry() {
  const queryClient = useQueryClient();

  return useMutation<SaveResult, Error, SaveVariables>({
    mutationFn: async ({ draft, recordId }) => {
      const paise = calcPaise(draft.calc);
      if (paise === null) throw new Error("Enter an amount");

      const req = entryBody(draft, recordId);
      const res = await apiFetch(apiUrl(req.domain, req.path), {
        method: req.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req.body),
      });
      if (!res.ok) throw new Error(await readError(res));

      // Create and update both answer with the saved row; its id is what the
      // custom-field values are written against, and what a later duplicate
      // deliberately does NOT reuse.
      const saved = (await res.json().catch(() => null)) as { id?: string } | null;
      return { id: String(saved?.id ?? recordId ?? ""), type: draft.type, amountPaise: paise };
    },
    onSuccess: () => {
      for (const key of AFFECTED_KEYS) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}
